import React, { useState, useEffect } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Download, FileText } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Banner, Navbar, Footer } from '../components/landing/Shared';
import { useAuth } from '../contexts/AuthContext';
import { AuthModal } from '../components/ui/authModal';

interface LegalDocPageProps {
    documentName: string;
}

const LegalDocPage: React.FC<LegalDocPageProps> = ({ documentName }) => {
    // We'll ignore the old theme context for the wrapper layout to enforce the new landing page style
    // but keep it for markdown if needed, though we will override styles.
    const { currentTheme } = useTheme();
    const navigate = useNavigate();
    const { isAuthenticated, logout } = useAuth();
    const [content, setContent] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string>('');

    // Auth modal state for Navbar
    const [showSignInModal, setShowSignInModal] = useState(false);
    const [modalMode, setModalMode] = useState<'signin' | 'signup'>('signin');

    useEffect(() => {
        const loadDocument = async () => {
            try {
                setLoading(true);
                setError('');

                // Fetch the markdown file from the public documents directory
                const response = await fetch(`/documents/${documentName}`);

                if (!response.ok) {
                    throw new Error(`Failed to load document: ${response.statusText}`);
                }

                const text = await response.text();
                setContent(text);
            } catch (err) {
                console.error('Error loading document:', err);
                setError(err instanceof Error ? err.message : 'Failed to load document');
            } finally {
                setLoading(false);
            }
        };

        loadDocument();
    }, [documentName]);

    const handleDownload = () => {
        const blob = new Blob([content], { type: 'text/markdown' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = documentName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    // Navbar handlers
    const handleSignIn = () => {
        setModalMode('signin');
        setShowSignInModal(true);
    };

    const handleSignUp = () => {
        setModalMode('signup');
        setShowSignInModal(true);
    };

    const handleSignOut = () => {
        logout();
    };

    const handleDashboard = () => {
        navigate('/dashboard');
    };

    // For "Download Add-in" in Navbar - redirect to home#download since it's on the main page
    const handleRunClick = () => {
        navigate('/#download');
    };

    return (
        <div className="min-h-screen bg-white text-[#111] antialiased font-sans">
            <Banner />
            <Navbar 
                onSignIn={handleSignIn} 
                onSignUp={handleSignUp} 
                onRunClick={handleRunClick}
                isAuthenticated={isAuthenticated}
                onSignOut={handleSignOut}
                onDashboard={handleDashboard}
            />
            
            <AuthModal
                showModal={showSignInModal}
                onClose={() => setShowSignInModal(false)}
                initialMode={modalMode}
            />

            <div className="pt-32 pb-20 max-w-4xl mx-auto px-6">
                <div className="mb-10 text-center">
                    <h1 className="text-4xl font-extrabold text-[#111] mb-4 tracking-tight">
                        {documentName.replace('.md', '').split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                    </h1>
                    <div className="h-1 w-20 bg-brand-blue mx-auto rounded-full opacity-20"></div>
                </div>

                {/* Download Button */}
                {!loading && !error && content && (
                    <div className="mb-8 flex justify-end">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={handleDownload}
                            className="text-zinc-500 border-zinc-200 hover:text-zinc-900 hover:bg-zinc-50 bg-white"
                        >
                            <Download className="w-4 h-4 mr-2" />
                            Download
                        </Button>
                    </div>
                )}

                <div className="bg-white border border-zinc-200 rounded-2xl shadow-sm p-8 md:p-12">
                    {loading && (
                        <div className="flex items-center justify-center py-20">
                            <div className="text-zinc-400 flex flex-col items-center animate-pulse">
                                <FileText className="w-12 h-12 mb-4" />
                                <p>Loading document...</p>
                            </div>
                        </div>
                    )}

                    {error && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
                            <p className="text-red-800 font-semibold mb-2">Error Loading Document</p>
                            <p className="text-red-600 text-sm">{error}</p>
                        </div>
                    )}

                    {!loading && !error && content && (
                        <article className="prose prose-zinc max-w-none 
                            prose-headings:font-bold prose-headings:text-zinc-900 
                            prose-h1:text-3xl prose-h2:text-2xl prose-h2:mt-8 prose-h2:mb-4 prose-h2:pb-2 prose-h2:border-b prose-h2:border-zinc-100
                            prose-p:text-zinc-600 prose-p:leading-relaxed 
                            prose-strong:text-zinc-900 
                            prose-ul:text-zinc-600 
                            prose-a:text-brand-blue prose-a:no-underline hover:prose-a:underline">
                            <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                {content}
                            </ReactMarkdown>
                        </article>
                    )}
                </div>
            </div>
            
            <Footer />
        </div>
    );
};

export default LegalDocPage;

